import { AxiosRequestConfig } from 'axios'

import { CreatePrizePayload, UpdatePrizeData } from './types'

const getPrizes = (): AxiosRequestConfig => ({
  url: '/prizes',
  method: 'GET',
})

const createPrize = (prize: CreatePrizePayload): AxiosRequestConfig => {
  const payload = new FormData()
  const {
    title,
    shortTitle,
    icon,
    image,
    level,
    welcomePrize,
    birthdayPrize,
    additionalText,
    membershipId,
    rewardType,
    pushTitle,
    pushDescription,
    pushMessage,
    expirationDays,
    tucanCategory,
    toteatCategory,
    storesEnabled,
    tucanEnabled,
    toteatEnabled,
    skus,
    toteatBenefitId,
    tagsSelected,
    membershipsSelected,
    allTagsAreRequired,
    timeRestriction,
    startDate,
    expiration,
  } = prize
  payload.append('title', title)
  if (shortTitle !== undefined && String(shortTitle).trim() !== '') {
    payload.append('shortTitle', shortTitle)
  }
  payload.append('icon', icon)
  if (image) payload.append('image', image)
  if (level) payload.append('level', level.toString())
  if (welcomePrize) payload.append('welcomePrize', welcomePrize.toString())
  if (birthdayPrize) payload.append('birthdayPrize', birthdayPrize.toString())
  if (additionalText) payload.append('additionalText', additionalText)
  if (membershipId) payload.append('membershipId', membershipId.toString())
  if (rewardType) payload.append('rewardType', rewardType)
  if (pushTitle) payload.append('pushTitle', pushTitle)
  if (pushDescription) payload.append('pushDescription', pushDescription)
  if (pushMessage) payload.append('pushMessage', pushMessage.toString())
  if (expirationDays) payload.append('expirationDays', expirationDays.toString())
  if (startDate) payload.append('startDate', startDate)
  if (expiration) payload.append('expiration', expiration)
  if (tucanCategory) payload.append('tucanCategory', tucanCategory)
  if (toteatCategory) payload.append('toteatCategory', toteatCategory)
  payload.append('storesEnabled', storesEnabled.toString())
  payload.append('tucanEnabled', tucanEnabled.toString())
  payload.append('toteatEnabled', toteatEnabled.toString())
  if (skus) payload.append('skus', skus.join(','))
  if (toteatBenefitId) payload.append('toteatBenefitId', toteatBenefitId)
  if (tagsSelected) payload.append('tags', tagsSelected.map(el => el.value).join(','))
  if (membershipsSelected && membershipsSelected.length > 0)
    payload.append('memberships', membershipsSelected.map(el => el.value).join(','))
  if (allTagsAreRequired !== undefined)
    payload.append('allTagsAreRequired', allTagsAreRequired.toString())
  if (timeRestriction) {
    payload.append('timeRestrictionEnabled', timeRestriction.timeRestrictionEnabled.toString())
    if (timeRestriction.allowedDays && timeRestriction.allowedDays.length > 0) {
      payload.append('allowedDays', JSON.stringify(timeRestriction.allowedDays))
    }
    if (timeRestriction.startTime) payload.append('startTime', timeRestriction.startTime)
    if (timeRestriction.endTime) payload.append('endTime', timeRestriction.endTime)
  }
  return {
    url: '/prizes',
    method: 'POST',
    data: payload,
  }
}

const updatePrize = (data: UpdatePrizeData): AxiosRequestConfig => {
  const payload = new FormData()
  const {
    title,
    shortTitle,
    icon,
    image,
    active,
    level,
    welcomePrize,
    additionalText,
    rewardType,
    pushTitle,
    pushDescription,
    pushMessage,
    expirationDays,
    tucanCategory,
    toteatCategory,
    storesEnabled,
    tucanEnabled,
    toteatEnabled,
    skus,
    toteatBenefitId,
    tagsSelected,
    membershipsSelected,
    allTagsAreRequired,
    timeRestriction,
    startDate,
    expiration,
  } = data.payload
  if (title) payload.append('title', title)
  if (shortTitle) payload.append('shortTitle', shortTitle)
  if (icon) payload.append('icon', icon)
  if (image) payload.append('image', image)
  if (active !== undefined) payload.append('active', active.toString())
  if (level) payload.append('level', level.toString())
  if (welcomePrize) payload.append('welcomePrize', true.toString())
  if (additionalText) payload.append('additionalText', additionalText)
  if (rewardType) payload.append('rewardType', rewardType)
  if (pushTitle) payload.append('pushTitle', pushTitle)
  if (pushDescription) payload.append('pushDescription', pushDescription)
  if (pushMessage !== undefined) payload.append('pushMessage', pushMessage.toString())
  if (expirationDays) payload.append('expirationDays', expirationDays.toString())
  if (startDate) payload.append('startDate', startDate)
  if (expiration) payload.append('expiration', expiration)
  if (tucanCategory) payload.append('tucanCategory', tucanCategory)
  if (toteatCategory) payload.append('toteatCategory', toteatCategory)
  if (storesEnabled !== undefined) payload.append('storesEnabled', storesEnabled.toString())
  if (tucanEnabled !== undefined) payload.append('tucanEnabled', tucanEnabled.toString())
  if (toteatEnabled !== undefined) payload.append('toteatEnabled', toteatEnabled.toString())
  if (skus) payload.append('skus', skus.join(','))
  if (toteatBenefitId) payload.append('toteatBenefitId', toteatBenefitId)
  if (tagsSelected) payload.append('tags', tagsSelected.map(el => el.value).join(','))
  if (membershipsSelected !== undefined) {
    if (membershipsSelected.length > 0) {
      payload.append('memberships', membershipsSelected.map(el => el.value).join(','))
    } else {
      payload.append('memberships', '')
    }
  }
  if (allTagsAreRequired !== undefined)
    payload.append('allTagsAreRequired', allTagsAreRequired.toString())
  if (timeRestriction) {
    payload.append('timeRestrictionEnabled', timeRestriction.timeRestrictionEnabled.toString())
    if (timeRestriction.allowedDays && timeRestriction.allowedDays.length > 0) {
      payload.append('allowedDays', JSON.stringify(timeRestriction.allowedDays))
    }
    if (timeRestriction.startTime) payload.append('startTime', timeRestriction.startTime)
    if (timeRestriction.endTime) payload.append('endTime', timeRestriction.endTime)
  }
  return {
    url: `/prizes/${data.id}`,
    method: 'PUT',
    data: payload,
  }
}

const deletePrize = (id: number): AxiosRequestConfig => ({
  url: `/prizes/${id}`,
  method: 'DELETE',
})

const searchPrizes = (query: string): AxiosRequestConfig => ({
  url: '/prizes/search',
  method: 'GET',
  params: { q: query },
})

export { getPrizes, createPrize, updatePrize, deletePrize, searchPrizes }
